import React, {
  useEffect,
  useState,
  useCallback,
  useImperativeHandle,
  useRef
} from "react";
import { Tree } from "antd";
import "../../css/companyTree.css";
import { TextField, Paper } from "@material-ui/core";
import * as localforage from "localforage";
import Search from "@material-ui/icons/Search";
import useStyles from "./styles";
import { useTranslation } from "../../langs/useTranslation";
import { getCompanyTreeAPI } from "../../apis/getCompanyTreeAPI";
import { useBoundingRect } from "../../helpers/useBoundingRect";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useUpdateEffect } from "ahooks";

const CompanyTree = React.forwardRef(
  (
    {
      excludeKeys,
      checkable = true,
      checkStrictly = true,
      onSelect,
      multiple,
      companyId,
      useParamId
    },
    ref
  ) => {
    const classes = useStyles();
    const { t } = useTranslation("accountCenter");
    const allKeys = useRef([]);
    const flattenCompanyTreeRef = useRef([]);
    const halfCheckedKeys = useRef([]);
    const [loading, setLoading] = useState(false);
    const [treeData, setTreeData] = useState([]);
    const [searchValue, setSearchValue] = useState("");
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [autoExpandParent, setAutoExpandParent] = useState(true);
    const [checkedKeys, setCheckedKeys] = useState([]);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [filteredTreeData, setFilteredTreeData] = useState([]);
    const timer = useRef(null);

    const [bbox, treeOuterRef] = useBoundingRect();

    const expKeys = new Set();
    const filterTreeBySearch = useCallback(
      data => {
        let tree = [];
        if (data) {
          tree = data
            .map(item => {
              const titleLowerCase = item.title.toLowerCase();
              const itemTitle = item.title;
              const num = item.num;
              const index = titleLowerCase.indexOf(searchValue.toLowerCase());
              const beforeStr = itemTitle.substr(0, index);
              const afterStr = itemTitle.substr(index + searchValue.length);
              const valueStr = itemTitle.substr(index, searchValue.length);

              let title = "";
              let childrenResult = filterTreeBySearch(item.children);

              if (index > -1) {
                if (searchValue !== "" && item.parent_id) {
                  expKeys.add(item.parent_id);
                }
                title = (
                  <span className="tree-outer">
                    {beforeStr}
                    <span className="site-tree-search-value">{valueStr}</span>
                    {afterStr}
                  </span>
                );
                return {
                  title,
                  key: item.key,
                  children: childrenResult,
                  name: itemTitle
                };
              } else if (childrenResult.length > 0) {
                if (searchValue !== "" && item.parent_id) {
                  expKeys.add(item.parent_id);
                }
                title = <span className="tree-outer">{item.title}</span>;
                return {
                  title,
                  key: item.key,
                  children: childrenResult,
                  name: itemTitle
                };
              } else {
                return null;
              }
            })
            .filter(item => item ?? false);

          return tree;
        } else {
          return [];
        }
      },
      [searchValue]
    );

    const getParentIds = (selfId, flattenData) => {
      const parentIds = [];
      function recursive(id) {
        for (const item of flattenData) {
          if (item.id === id) {
            parentIds.push(item.parent_company_id);
            recursive(item.parent_company_id);
            break;
          }
        }
      }
      recursive(selfId);
      return parentIds;
    };

    const recursiveGetChildren = startNode => {
      const results = [];
      function recursive(node) {
        if (node.children) {
          node.children.forEach(item => {
            results.push(item.key);
            recursive(item);
          });
        }
      }

      recursive(startNode);

      return results;
    };

    const getTreeData = async () => {
      try {
        setLoading(true);
        await getCompanyTreeAPI(false, companyId, useParamId);
        const companyTree = await localforage.getItem("companyTree");
        const flattenCompanyTree = await localforage.getItem(
          "flattenCompanyTree"
        );
        flattenCompanyTreeRef.current = flattenCompanyTree;
        setTreeData(companyTree);
        setExpandedKeys(companyTree.map(d => d.key));
        const _allKeys = flattenCompanyTree.map(item => item.id);
        if (excludeKeys) {
          // if (!checkStrictly) {
          //   // excludeKeys包含自己和它所有父级节点id
          //   excludeKeys = excludeKeys.concat(
          //     excludeKeys.reduce(
          //       (p, v) => p.concat(getParentIds(v, flattenCompanyTree)),
          //       []
          //     )
          //   );
          //   // 去重
          //   excludeKeys = Array.from(new Set(excludeKeys));
          // }
          // 从所有keys中过滤掉被排除的key
          const allKeysWithoutExcludes = _allKeys.filter(
            key => !excludeKeys.includes(key)
          );
          setCheckedKeys(allKeysWithoutExcludes);
        } else {
          setCheckedKeys(_allKeys);
        }
        allKeys.current = _allKeys;
      } finally {
        setLoading(false);
      }
    };

    const onExpand = expandedKeys => {
      setExpandedKeys(expandedKeys);
      setAutoExpandParent(false);
    };

    const onChange = e => {
      const { value } = e.target;
      setSearchValue(value);
    };

    const onCheck = (checkedKeys, event) => {
      if (checkStrictly) {
        const nodeChildrenkeys = recursiveGetChildren(event.node);
        if (event.checked) {
          const parentKeys = getParentIds(
            event.node.key,
            flattenCompanyTreeRef.current
          );
          setCheckedKeys(
            Array.from(
              new Set([
                ...event.checkedNodes.map(node => node.key),
                ...nodeChildrenkeys,
                ...parentKeys
              ])
            )
          );
        } else {
          setCheckedKeys(prevChecks =>
            prevChecks.filter(
              key => key !== event.node.key && !nodeChildrenkeys.includes(key)
            )
          );
        }
      } else {
        setCheckedKeys(event.checkedNodes.map(node => node.key));
      }

      halfCheckedKeys.current = event.halfCheckedKeys || [];
    };

    const handleSelect = (selectedKeys, info) => {
      setSelectedKeys(selectedKeys);
      onSelect?.(selectedKeys, info);
    };

    const clearTimer = () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };

    useImperativeHandle(ref, () => {
      return {
        getCheckedKeys: () => {
          return checkedKeys;
        },
        getAllKeys: () => {
          return allKeys.current;
        },
        getExcludeKeys: () => {
          return allKeys.current.filter(
            item =>
              !checkedKeys.includes(item) &&
              !halfCheckedKeys.current.includes(item)
          );
        }
      };
    });

    useEffect(() => {
      getTreeData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useUpdateEffect(() => {
      timer.current = setTimeout(() => {
        setFilteredTreeData(filterTreeBySearch(treeData));
        setExpandedKeys(Array.from(expKeys));
      }, 200);

      return clearTimer;
    }, [searchValue]);

    const finalTreeData = !!searchValue ? filteredTreeData : treeData;

    return (
      <>
        <div className={classes.searchInput}>
          <TextField
            label={t("accountCenter.searchBarFieldDefaultText")}
            variant="outlined"
            size="small"
            onChange={onChange}
          />
          <Search className={classes.searchIcon} color="action" />
        </div>
        <Paper elevation={0} className={classes.companyTreeContent}>
          {loading ? (
            <CircularProgress size={30} className={classes.loading} />
          ) : finalTreeData.length > 0 ? (
            <div
              className={classes.treeOuter}
              id="treeOuter"
              ref={treeOuterRef}
            >
              <Tree
                checkable={checkable}
                checkStrictly={checkStrictly}
                defaultExpandAll={true}
                showIcon={false}
                onExpand={onExpand}
                defaultSelectedKeys={[]}
                treeData={finalTreeData}
                expandedKeys={expandedKeys}
                autoExpandParent={autoExpandParent}
                onSelect={handleSelect}
                selectedKeys={selectedKeys}
                onCheck={onCheck}
                checkedKeys={checkedKeys}
                multiple={multiple}
                height={bbox.height ? bbox.height : 390}
              />
            </div>
          ) : (
            <div style={{ textAlign: "center" }}>
              <p>No matched result.</p>
            </div>
          )}
        </Paper>
      </>
    );
  }
);

export default CompanyTree;
